import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
  Button,
  Checkbox,
  IconButton,
  List,
  MenuItem,
  Select
} from '@mui/material';
import { Skeleton } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { SkeletonRounded } from 'modules/loading/SkeletonRounded';
import { useAutoPilotEnabled } from 'modules/rootPage/hooks/useAutoPilotEnabled';
import { useSmartDiaryCheckboxIcons } from './hooks/useSmartDiaryCheckboxIcons';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '98%',
    margin: theme.spacing(1.5, 0, 1, 0)
  },

  summary: {
    display: 'flex',
    marginTop: theme.spacing(-1),
    justifyContent: 'space-between',
    paddingLeft: ({ isAutoPilotEnabled }: { isAutoPilotEnabled: boolean }) =>
      isAutoPilotEnabled ? theme.spacing(2) : theme.spacing(0)
  },

  tabContainer: {
    display: 'flex',
    margin: theme.spacing(0, 0, 1, 2),
    justifyContent: 'space-between',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.5)
  },

  tab: {
    width: '25%',
    height: theme.spacing(5),
    background: theme.skelton?.color,
    margin: theme.spacing(0, 1),
    padding: theme.spacing(1)
  },

  contentLeft: {
    display: 'flex',
    width: '40%',
    alignItems: 'center',
    paddingLeft: ({ isAutoPilotEnabled }: { isAutoPilotEnabled: boolean }) =>
      isAutoPilotEnabled ? theme.spacing(2) : theme.spacing(0)
  },

  contentRight: {
    display: 'flex',
    width: '30%',
    justifyContent: 'flex-end',
    paddingRight: theme.spacing(1),
    alignItems: 'center'
  },

  noteContainer: {
    display: 'flex',
    width: '95%',
    margin: theme.spacing(-0.5, 0, -0.5)
  },

  detailsMain: {
    background: theme.skelton?.color,
    width: theme.spacing(20),
    margin: theme.spacing(0.5),
    height: theme.spacing(3.5)
  },

  details: {
    background: theme.skelton?.color,
    width: theme.spacing(10),
    margin: theme.spacing(0.5),
    height: theme.spacing(3.5)
  },

  detailsRight: {
    background: theme.skelton?.color,
    width: theme.spacing(10),
    margin: theme.spacing(0.5, 4.75, 0.5, 0.5),
    height: theme.spacing(3.5)
  },

  detailsSummary: {
    background: theme.skelton?.color,
    width: theme.spacing(10),
    margin: theme.spacing(0.5),
    height: theme.spacing(3.5)
  },

  checkbox: {
    alignSelf: 'center',
    marginLeft: theme.spacing(0),
    marginTop: theme.spacing(0.25),
    marginRight: theme.spacing(-0.5)
  },

  smartDiaryLeft: {
    display: 'flex',
    width: '50%',
    alignItems: 'center'
  },

  collapseExpandDay: {
    background: theme.skelton?.color,
    width: theme.spacing(3.5),
    height: theme.spacing(3.5),
    marginLeft: theme.spacing(0.2)
  },

  noteDate: {
    display: 'flex',
    background: '#e0e0e0',
    justifyContent: 'space-between'
  },

  notes: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%'
  },

  noteText1: {
    background: theme.skelton?.color,
    width: '80%',
    height: theme.spacing(3.5),
    margin: theme.spacing(1, 0.5, 0.5)
  },

  noteText2: {
    background: theme.skelton?.color,
    width: '80%',
    height: theme.spacing(3.5),
    margin: theme.spacing(-1.5, 0.5, 0.5)
  },

  bottomContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    height: '100%',
    alignItems: 'flex-end',
    padding: theme.spacing(1, 1, 2, 1.5)
  },

  lastRow: {
    display: 'flex',
    flexDirection: 'column',
    width: '50%'
  },

  lastUpdatedText: {
    marginBottom: theme.spacing(-0.75),
    background: theme.skelton?.color,
    width: '55%',
    height: theme.spacing(3.25)
  },

  applyTimesheet1: {
    background: theme.palette.grey[300],
    width: '40%',
    height: theme.spacing(5),
    padding: theme.spacing(1, 4, 1, 4)
  },

  applyTimesheet2: {
    background: theme.skelton?.color,
    width: '90%',
    height: theme.spacing(4.5)
  },

  firstRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%'
  },
  iconButton: {
    color: theme.palette.grey[300]
  },
  iconButtonBack: {
    height: theme.spacing(3)
  },
  iconButtonForward: {
    height: theme.spacing(3)
  },
  selector: {
    display: 'flex',
    marginRight: theme.spacing(-6.5),
    height: theme.spacing(5),
    width: '35%'
  },
  menuItem: {
    display: 'flex',
    background: theme.skelton?.color,
    width: '100%',
    height: theme.spacing(3.75),
    marginLeft: theme.spacing(-1),
    marginRight: theme.spacing(1)
  },
  smartDiaryHeader: {
    display: 'flex',
    width: '90%',
    marginTop: theme.spacing(0.25),
    marginBottom: theme.spacing(2)
  },
  nagivator: {
    display: 'flex',
    width: '100%',
    marginRight: theme.spacing(2)
  }
}));

type SmartDiaryNotesSkeletonProps = {
  showHeaders?: boolean;
};

export const SmartDiaryNotesSkeleton: React.FC<SmartDiaryNotesSkeletonProps> = ({
  showHeaders = true
}: SmartDiaryNotesSkeletonProps) => {
  const iconProps = useSmartDiaryCheckboxIcons();

  const isAutoPilotEnabled = useAutoPilotEnabled();
  const classes = useStyles({ isAutoPilotEnabled });

  return (
    <div className={classes.container} data-testid="smart-diary-notes-skeleton">
      {showHeaders && (
        <div className={classes.smartDiaryHeader}>
          <div className={classes.firstRow}>
            <div className={classes.nagivator}>
              <IconButton className={classes.iconButton} disabled>
                <ChevronLeftIcon className={classes.iconButtonBack} />
              </IconButton>
              <IconButton className={classes.iconButton} disabled>
                <ChevronRightIcon className={classes.iconButtonForward} />
              </IconButton>
              <SkeletonRounded width={'55%'} height={35} />
            </div>
            <Select displayEmpty className={classes.selector}>
              <MenuItem>
                <Skeleton className={classes.menuItem}></Skeleton>
              </MenuItem>
            </Select>
          </div>
        </div>
      )}
      <div className={classes.tabContainer}>
        {[...Array(3)].map((_, index) => (
          <Skeleton key={index} className={classes.tab}></Skeleton>
        ))}
      </div>
      <div className={classes.summary}>
        <div className={classes.contentLeft}>
          {!isAutoPilotEnabled && (
            <Checkbox
              disabled
              className={classes.checkbox}
              color="default"
              checked
              {...iconProps}
            ></Checkbox>
          )}
          {!isAutoPilotEnabled && (
            <Skeleton className={classes.detailsMain}></Skeleton>
          )}
        </div>
        <div className={classes.contentRight}>
          <Skeleton className={classes.detailsRight}></Skeleton>
        </div>
      </div>
      <div className={classes.noteDate}>
        <div className={classes.contentLeft}>
          {!isAutoPilotEnabled && (
            <Checkbox
              disabled
              className={classes.checkbox}
              color="default"
              checked
              {...iconProps}
            ></Checkbox>
          )}
          <Skeleton className={classes.details}></Skeleton>
        </div>
        <div className={classes.contentRight}>
          <Skeleton className={classes.detailsSummary}></Skeleton>
          <IconButton size="small">
            <ExpandLessIcon />
          </IconButton>
        </div>
      </div>

      <List>
        {[...Array(2)].map((_: number, rowIndex: number) => (
          <div key={`noteList${rowIndex}`} className={classes.summary}>
            {!isAutoPilotEnabled && (
              <Checkbox
                disabled
                className={classes.checkbox}
                color="default"
                checked
                {...iconProps}
              ></Checkbox>
            )}
            <div className={classes.notes}>
              <Skeleton className={classes.noteText1}></Skeleton>
              <Skeleton className={classes.noteText2}></Skeleton>
            </div>
            <div className={classes.contentRight}>
              <Skeleton className={classes.detailsRight}></Skeleton>
            </div>
          </div>
        ))}
      </List>
      <div className={classes.bottomContainer}>
        <div className={classes.lastRow}>
          <Skeleton className={classes.lastUpdatedText} />
          <Skeleton className={classes.lastUpdatedText} />
        </div>
        {!isAutoPilotEnabled && (
          <Button className={classes.applyTimesheet1}>
            <Skeleton className={classes.applyTimesheet2} />
          </Button>
        )}
      </div>
    </div>
  );
};
